
import { Database, ShoppingCart, ShieldCheck, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const EcommerceLLM = () => {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-12 max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-benchr-900 dark:text-white mb-6">
          Best E-commerce LLM Technology
        </h2>
        <p className="text-lg text-benchr-600 dark:text-benchr-300">
          Our enterprise-grade infrastructure processes product descriptions, reviews, and market data in real-time while routing questions to the optimal LLM for guaranteed accuracy.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {/* Card 1: Real-time Data Processing */}
        <div className="bg-white dark:bg-benchr-800 rounded-xl p-6 shadow-lg border border-benchr-100 dark:border-benchr-700/40 relative overflow-hidden">
          <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-gradient-to-br from-benchr-100/60 dark:from-benchr-600/10 to-transparent rounded-bl-full"></div>
          
          <h3 className="text-2xl font-bold text-benchr-600 mb-6">Real-time Product Processing</h3>
          
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-6 h-6 text-benchr-600" />
              <span className="font-semibold">Real-time Ingestion</span>
            </div>
            
            <div className="space-y-2 mt-4">
              <div className="inline-block bg-benchr-100 dark:bg-benchr-700/40 text-benchr-700 dark:text-benchr-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">Product Listings</div>
              <div className="inline-block bg-benchr-100 dark:bg-benchr-700/40 text-benchr-700 dark:text-benchr-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">Customer Reviews</div>
              <div className="inline-block bg-benchr-100 dark:bg-benchr-700/40 text-benchr-700 dark:text-benchr-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">Market Reports</div>
            </div>
            
            <p className="mt-4 text-benchr-600 dark:text-benchr-300">
              <span className="font-bold text-green-600">+1.3B</span> products, 3B reviews, 15TB data processed
            </p>
          </div>
        </div>
        
        {/* Card 2: Best LLM Models */}
        <div className="bg-white dark:bg-benchr-800 rounded-xl p-6 shadow-lg border border-benchr-100 dark:border-benchr-700/40 relative overflow-hidden">
          <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-gradient-to-br from-benchr-100/60 dark:from-benchr-600/10 to-transparent rounded-bl-full"></div>
          
          <h3 className="text-2xl font-bold text-benchr-600 mb-6">Best LLM Models</h3>
          
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <ShoppingCart className="w-6 h-6 text-benchr-600" />
              <span className="font-semibold">Multi-Model Routing</span>
            </div>
            
            <div className="space-y-2 mt-4">
              <div className="inline-flex items-center bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">
                GPT-4 Turbo
              </div>
              <div className="inline-flex items-center bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">
                Claude 3 Opus
              </div>
              <div className="inline-flex items-center bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">
                Gemini 1.5 Pro
              </div>
              <div className="inline-flex items-center bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">
                Llama
              </div>
            </div>
            
            <p className="mt-4 text-benchr-600 dark:text-benchr-300">
              Dynamic optimization for performance and cost with specialized e-commerce fine-tuning
            </p>
          </div>
        </div>
        
        {/* Card 3: Zero Hallucination */}
        <div className="bg-white dark:bg-benchr-800 rounded-xl p-6 shadow-lg border border-benchr-100 dark:border-benchr-700/40 relative overflow-hidden">
          <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-gradient-to-br from-benchr-100/60 dark:from-benchr-600/10 to-transparent rounded-bl-full"></div>
          
          <h3 className="text-2xl font-bold text-benchr-600 mb-6">0 Hallucination</h3>
          
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-6 h-6 text-benchr-600" />
              <span className="font-semibold">Verification System</span>
            </div>
            
            <div className="mt-4 font-mono text-sm p-3 bg-benchr-50 dark:bg-benchr-900/40 rounded-md overflow-x-auto">
              <div className="text-blue-600 dark:text-blue-400">agent_1 [retriever]: Located product</div>
              <div className="text-blue-600 dark:text-blue-400">metadata SKU 23456 inventory status</div>
              <div className="text-violet-600 dark:text-violet-400">agent_2 [validator]: Verified $129.99</div>
              <div className="text-violet-600 dark:text-violet-400">pricing matches marketplace sources</div>
              <div className="text-green-600 dark:text-green-400">agent_3 [fact_checker]: Confirmed</div>
              <div className="text-green-600 dark:text-green-400">availability and shipping estimates</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceLLM;
